/**
 * Terms and privacy, in the site's own words: the facts are public records
 * shown as filed, each source's terms travel with its facts, the derived
 * facts carry one permissive license, and the site keeps nothing about a
 * reader beyond what hosting requires. Written to close the legal exposure
 * of running the site, not to make promises the site cannot keep.
 */
import { COPY } from '../../copy.js';
import type { DocPage } from '../types.js';

export const termsPage: DocPage = {
  slug: 'terms',
  title: 'Terms and privacy',
  audience: 'everyone',
  promise: 'What the facts are, what the site does not promise, what it keeps about you, and how to ask for a correction.',
  lead: 'The facts on this site are public records shown as they were filed or published, with their sources beside them. This page says what that means for you, what the site keeps, and how to ask for a correction.',
  sections: [
    {
      id: 'facts',
      heading: 'The facts',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Every fact on the site is shown as it was filed with the IRS, published by a funder, or recorded by a federal agency, with its source and its period beside it. FunderGraph does not check a filing against the world. A return can be wrong, late, amended, or absent, and the site shows what the records show. Nothing on the site is advice, a rating, or a judgment of any organization, and the site carries no warranty of any kind.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'Some facts are the site’s own: a recipient name matched to an organization, a shared-funder map, a search ranking. Each of these says how it was computed and from what, on ',
            { link: '/about/reading', text: 'the reading page' },
            ', and each can be wrong in the ways that page describes.',
          ],
        },
      ],
    },
    {
      id: 'sources',
      heading: 'Each source’s terms',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The returns are public records the IRS publishes under the law that opens them, and FunderGraph treats them as public domain, as its catalog of sources records; the master file and the federal award records are works of the United States government. Grant lists read from a funder’s own website are collected under that site’s terms and robots directives, and only the facts read from a page are shown: the funder, the recipient as written, the amount, the year, the program, and the purpose. Page content is not republished. The source is named beside every fact; its terms are recorded in the catalog and will be set out on a page of sources.',
          ],
        },
      ],
    },
    {
      id: 'derived',
      heading: 'Derived facts',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The facts FunderGraph computes, resolution links, shared-funder maps, and search rankings, are offered under ',
            { link: 'https://creativecommons.org/licenses/by/4.0/', text: 'CC BY 4.0' },
            ': use them and cite FunderGraph and the page. The filed facts beneath them keep the terms of their sources.',
          ],
        },
      ],
    },
    {
      id: 'privacy',
      heading: 'What the site keeps about you',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The site has no accounts today and sets no tracking cookie. It keeps, in your browser only and for this tab only, the trail of maps you explored, so you can walk back; it is gone when the tab closes. The services behind the site keep the error logs they need to run, and the limit in front of the API keeps counts, not requests. A message sent to hello@fundergraph.org is kept for thirty days on the mail service and then deleted. When accounts arrive, this page will say what they store before they are offered.',
          ],
        },
      ],
    },
    {
      id: 'corrections',
      heading: 'Corrections',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A fact that is wrong on the site is either wrong in the record or wrong in how the site read it. Either way, write to ',
            { link: `mailto:${COPY.site.contact}`, text: COPY.site.contact },
            ' with the page, the fact, and what the record says instead. A group that appears only inside another organization’s filing, such as a sponsored project, is shown in that filing’s context; ',
            { link: '/about/reading#listing', text: 'the listing rule' },
            ' says why.',
          ],
        },
      ],
    },
  ],
};
